/**
 * The quantity model: handover rev7 §5.3 (pre-selection), §5.6 (fruit and
 * drinks), §6.1–§6.7, with `build/FIXES.md` item 2 in place of §5.5.
 *
 * Pure functions. Every figure is passed in; nothing here holds a parameter
 * value, an occasion point, a price or a production unit.
 */

import {
  ZERO,
  ONE,
  add,
  ceilToInteger,
  ceilToUnits,
  compare,
  divide,
  floorToInteger,
  floorToUnits,
  fromDecimal,
  isZero,
  multiply,
  rational,
  subtract,
  type Rational,
} from './rational.ts'
import type {
  Balance,
  Dish,
  DrinkSelection,
  FruitItem,
  Occasion,
  QuantityParams,
  Side,
} from './types.ts'

const HUNDRED = rational(100n)

/** The index of the band `guests` falls in, given the two upper bounds. */
export function bandIndex(guests: number, bands: readonly [number, number]): 0 | 1 | 2 {
  return guests <= bands[0] ? 0 : guests <= bands[1] ? 1 : 2
}

/** §6.4: the band of the **whole** guest count decides every margin rate. */
export const marginBand = (guests: number, params: QuantityParams): 0 | 1 | 2 =>
  bandIndex(guests, params.marginBands)

/** §6.6: the headcount band that sets the page limit. Not the margin band. */
export const dishBand = (guests: number, params: QuantityParams): 0 | 1 | 2 =>
  bandIndex(guests, params.dishBands)

/** §6.4: the rate this dish takes, in per cent. Featured dishes take their own. */
export function marginRatePct(dish: Dish, guests: number, params: QuantityParams): Rational {
  const band = marginBand(guests, params)
  const rates = dish.featured ? params.marginFeaturedPct : params.marginStandardPct
  return rates[band]
}

function lookup(table: Readonly<Record<string, Rational>>, key: string, what: string): Rational {
  const value = table[key]
  if (value === undefined) throw new Error(`${what}: no entry for ${JSON.stringify(key)}`)
  return value
}

/**
 * §6.7 step 1, before the balance: the occasion point × the serving-window
 * factor (§6.2) × the guest profile (§6.3) × the buffet uplift (§6.5, 1,0).
 * The multipliers stack, as §6.3 decided for now.
 *
 * **Run 18 item R18-1: the serving-window factor is a ratio.** A figure set
 * is measured at some serving window — the occasion's `referenceWindow` —
 * and the factor is `mult(chosen) ÷ mult(reference)`, so the figures are
 * unchanged at their own band and move only as the event runs longer or
 * shorter than it. The evening reception's reference band is `2h`, whose
 * multiplier is 1,0, so this is exactly what it has always done there. An
 * occasion with no reference window takes no duration at all and the factor
 * is 1.
 */
export function basePiecesPerGuest(
  occasion: Occasion,
  input: { servingWindow: string; profile: string },
  params: QuantityParams,
  side: Side,
): Rational {
  const point = side === 'sav' ? occasion.savouryPoint : occasion.sweetPoint
  const window = servingWindowFactor(occasion, input.servingWindow, params)
  const profile = lookup(params.profileMultipliers, input.profile, 'guest profile')
  return multiply(multiply(multiply(point, window), profile), params.buffetSelfServiceMultiplier)
}

/**
 * §6.2 as R18-1 leaves it: the chosen band's multiplier over the figure
 * set's own band's. Both are looked up by name, so a reference band the
 * register does not hold fails by name rather than defaulting to one.
 */
export function servingWindowFactor(
  occasion: Occasion,
  servingWindow: string,
  params: QuantityParams,
): Rational {
  if (occasion.referenceWindow === null) return ONE
  const chosen = lookup(params.servingWindowMultipliers, servingWindow, 'serving window')
  const reference = lookup(
    params.servingWindowMultipliers,
    occasion.referenceWindow,
    'reference serving window',
  )
  if (isZero(reference)) {
    throw new Error(`reference serving window: ${JSON.stringify(occasion.referenceWindow)} is zero`)
  }
  return divide(chosen, reference)
}

/**
 * `build/FIXES.md` item 2, which overrides §5.5: the savoury/sweet balance
 * moves `balanceShift` pieces per guest from one side to the other. The total
 * per guest is unchanged and neither side goes below `balanceFloor` — the
 * shift is cut short at the floor rather than refused. Both values are
 * PROPOSED, for Balázs to calibrate; they live in the register, not here.
 */
export function applyBalance(
  savoury: Rational,
  sweet: Rational,
  balance: Balance,
  params: QuantityParams,
): { savoury: Rational; sweet: Rational } {
  if (balance !== 'sav' && balance !== 'sw') return { savoury, sweet }
  const giving = balance === 'sav' ? sweet : savoury
  const room = subtract(giving, params.balanceFloor)
  const move =
    compare(room, ZERO) <= 0 ? ZERO : compare(room, params.balanceShift) < 0 ? room : params.balanceShift
  return balance === 'sav'
    ? { savoury: add(savoury, move), sweet: subtract(sweet, move) }
    : { savoury: subtract(savoury, move), sweet: add(sweet, move) }
}

/** §6.7 step 1 with the balance of `build/FIXES.md` item 2 applied. */
export function piecesPerGuest(
  occasion: Occasion,
  input: { servingWindow: string; profile: string; balance: Balance },
  params: QuantityParams,
): { savoury: Rational; sweet: Rational } {
  return applyBalance(
    basePiecesPerGuest(occasion, input, params, 'sav'),
    basePiecesPerGuest(occasion, input, params, 'sw'),
    input.balance,
    params,
  )
}

/**
 * The three words `orders.qty_level_sav` and `orders.qty_level_sw` hold. 021
 * kept the columns and gave them to the balance: a side the balance favours
 * is `more`, the side that gives is `less`, and the recommended balance
 * leaves both `suggested`.
 */
export function balanceToQtyLevels(balance: Balance): {
  sav: 'less' | 'suggested' | 'more'
  sw: 'less' | 'suggested' | 'more'
} {
  if (balance === 'sav') return { sav: 'more', sw: 'less' }
  if (balance === 'sw') return { sav: 'less', sw: 'more' }
  return { sav: 'suggested', sw: 'suggested' }
}

/** The inverse, so a stored order reads back as the balance that produced it. */
export function qtyLevelsToBalance(levels: { sav: string; sw: string }): Balance {
  if (levels.sav === 'more' && levels.sw === 'less') return 'sav'
  if (levels.sav === 'less' && levels.sw === 'more') return 'sw'
  return 'rec'
}

export interface DishCountSuggestion {
  /** The occasion range of §6.6, trimmed to the limits below. */
  range: [number, number]
  /** §6.6 as run 19 §7 leaves it: the **top** of the trimmed range. */
  preselect: number
  /**
   * Run 19 §7: the low end of the trimmed range — what was pre-selected
   * before this run, and the floor `preselectCount`'s guard stops at.
   */
  preselectFloor: number
  /** min(page limit by headcount, kitchen ceiling). A hard cap. */
  limit: number
  /** True where the page limit or the ceiling moved the occasion range. */
  trimmed: boolean
}

/**
 * §6.6: the occasion range trimmed to the headcount page limit and Robi's
 * kitchen ceiling. The page limit wins over the range (DECIDED 2026. 10.
 * 03.), and a range that collapses to one number is one number.
 *
 * **Run 19 §7 (DECIDED 20261016, Barna): the *top* of the trimmed range is
 * pre-selected, not the low end.** The starting menu offered fewer dishes
 * than the kitchen actually sends, which undersells the brand; Ágó's test of
 * 16 October is what found it. The page limit and the kitchen ceiling still
 * bind, because they are what `range` is already trimmed to.
 *
 * `preselectFloor` is what §7's guard may fall back to — the low end, which
 * is what was pre-selected before this run (assumption A17 `preselectCount`).
 */
export function dishCountSuggestion(
  occasion: Occasion,
  guests: number,
  side: Side,
  params: QuantityParams,
): DishCountSuggestion {
  const band = dishBand(guests, params)
  const limit =
    side === 'sav'
      ? Math.min(params.dishLimitSavoury[band], params.kitchenCeilingSavoury)
      : Math.min(params.dishLimitSweet[band], params.kitchenCeilingSweet)
  const source = side === 'sav' ? occasion.savouryDishRange : occasion.sweetDishRange
  const range: [number, number] = [Math.min(source[0], limit), Math.min(source[1], limit)]
  return {
    range,
    preselect: range[1],
    preselectFloor: range[0],
    limit,
    trimmed: range[0] !== source[0] || range[1] !== source[1],
  }
}

/**
 * **Run 19 §7's guard (PROPOSED 20261018).** How many dishes the menu
 * actually opens with on one side, once the top of the range has been asked
 * for.
 *
 * At a small headcount more dishes means less of each, and §6.7 step 5
 * rounds every line up to its own production unit — so asking for the top of
 * the range can make the order *larger* than the recommendation rather than
 * better spread. The guard drops dishes from the **end of the preference
 * order** while any pre-selected dish's share of the recommended pieces is
 * below one production unit of that dish.
 *
 * The share is §6.7 step 2's: the side total split equally across the dishes
 * chosen, **before** the §6.4 margin — the figure the recommendation is, not
 * the figure the kitchen makes. Comparing against the margined figure would
 * let the margin, which is production's and not the customer's, decide how
 * many dishes a customer is offered.
 *
 * It never goes below the low end of the occasion's own range
 * (`preselectFloor`): that is what the page pre-selected before run 19, and
 * opening a small order on a **narrower** menu than it had is a change
 * nobody asked for. **Assumption A17 `preselectCount`.**
 *
 * `ranked` is the §5.3 preference order for the side, which is why dropping
 * from its end is dropping the least-preferred dish.
 */
export function preselectCount(
  ranked: readonly Dish[],
  occasion: Occasion,
  input: { servingWindow: string; profile: string; balance: Balance },
  guests: number,
  side: Side,
  params: QuantityParams,
): number {
  const suggestion = dishCountSuggestion(occasion, guests, side, params)
  const wanted = Math.min(suggestion.preselect, ranked.length)
  const floor = Math.min(suggestion.preselectFloor, ranked.length)
  if (wanted <= floor) return wanted
  const perGuest = piecesPerGuest(occasion, input, params)
  const sideTotal = multiply(rational(guests), side === 'sav' ? perGuest.savoury : perGuest.sweet)
  /* A side with nothing on it has nothing to spread, and dividing by the
     count would say every dish is short. The top of the range stands. */
  if (isZero(sideTotal)) return wanted
  let count = wanted
  while (count > floor) {
    const share = divide(sideTotal, rational(count))
    const short = ranked
      .slice(0, count)
      .some((dish) => compare(share, rational(Math.max(Math.round(dish.productionUnit), 1))) < 0)
    if (!short) break
    count -= 1
  }
  return count
}

/**
 * §5.3 pre-selection order (PROPOSED 2026. 10. 03.): café dishes, the
 * occasion leading dishes, favourites, dishes kept on the menu by decision,
 * then catalogue order. `dishes` arrives in catalogue order and the sort is
 * stable, so the last rank is the order it came in.
 */
export function rankForPreselection(dishes: readonly Dish[], occasion: Occasion, side: Side): Dish[] {
  const leading = occasion.leadingDishes?.[side] ?? []
  const key = (dish: Dish, index: number): number[] => [
    dish.cafe ? 0 : 1,
    leading.indexOf(dish.id) === -1 ? leading.length : leading.indexOf(dish.id),
    dish.featured ? 0 : 1,
    dish.keptByDecision ? 0 : 1,
    index,
  ]
  return dishes
    .map((dish, index) => ({ dish, key: key(dish, index) }))
    .sort((a, b) => {
      for (let i = 0; i < a.key.length; i++) {
        const left = a.key[i] ?? 0
        const right = b.key[i] ?? 0
        if (left !== right) return left - right
      }
      return 0
    })
    .map((entry) => entry.dish)
}

/**
 * §5.3 and §6.6 together: the dishes the menu opens with on one side. The
 * published dishes for the date, ranked, cut to the pre-selection count.
 */
export function preselectDishes(
  published: readonly Dish[],
  occasion: Occasion,
  guests: number,
  side: Side,
  params: QuantityParams,
  /**
   * Run 19 §7: the guard needs the answers the recommendation is worked out
   * from. Omitted — every caller that only wants the rank — the count is the
   * top of the range with no guard, which is what the suggestion says on its
   * own.
   */
  input?: { servingWindow: string; profile: string; balance: Balance },
): Dish[] {
  const onSide = published.filter((dish) => dish.side === side)
  const ranked = rankForPreselection(onSide, occasion, side)
  const count =
    input === undefined
      ? dishCountSuggestion(occasion, guests, side, params).preselect
      : preselectCount(ranked, occasion, input, guests, side, params)
  return ranked.slice(0, count)
}

/** One dish with its exact share, its margin and the quantity made. */
export interface SideLine {
  dish: Dish
  /** Guests × pieces per guest × weight ÷ total weight. */
  share: Rational
  /** The share with its margin, before rounding (§6.7 step 4). */
  shareWithMargin: Rational
  marginPct: Rational
  /** The quantity made, delivered and charged (§6.7 step 5). */
  portions: number
  productionUnits: number
  /**
   * Run 15 item 1(d): what this line would be with no customer quantity on
   * it, at the headcount, appetite and balance in force now. Equal to
   * `portions` on a line the customer has not touched.
   */
  recommendedPortions: number
  /** Run 15 item 1(j): the customer set this quantity with − / +. */
  customerSet: boolean
  /**
   * Run 9, item 5: on a gluten-free line, what goes into the labelled box —
   * gluten-free guests × pieces per guest, rounded up once and split across
   * the box dishes. `portions` can be more, when a dish added for the box
   * alone is rounded up to whole production units; this is what the
   * customer's gluten-free card shows, never the batch.
   */
  boxPortions?: number
  /**
   * **Run 17 item B5(b) (DECIDED 20261015, Barna):** the part of this line's
   * `portions` that belongs to the gluten-free guests.
   *
   * *Cover from the menu first.* Where a gluten-free, verified dish is
   * already in the main selection on this side, the gluten-free guests'
   * share is **added to that dish's own quantity** rather than drawn as a
   * second line: one line, one quantity, with the share recorded here. The
   * customer reads *ebből {n} db gluténmentes vendégeknek* beside it and the
   * kitchen packs that many into their own labelled gluten-free box
   * (assumption A5).
   *
   * On a line of `glutenFreeLines` — a separate gluten-free line, drawn only
   * for a shortfall or where no gluten-free dish is on the side — this
   * equals `portions`: the whole line is the gluten-free guests'.
   */
  glutenFreePortions?: number
}

/**
 * §6.7 steps 2–5 for one side: the side total split by weight across the
 * dishes, margin per dish at the band of the whole guest count, then up to
 * whole production units. `marginGuests` is the whole guest count even when
 * `guests` is only the gluten-free part of it (§6.7 step 6).
 */
function recommendedLines(
  dishes: readonly Dish[],
  piecesPer: Rational,
  guests: number,
  options: { marginGuests: number },
  params: QuantityParams,
): SideLine[] {
  if (dishes.length === 0) return []
  const totalWeight = rational(dishes.length)
  const sideTotal = multiply(rational(guests), piecesPer)
  return dishes.map((dish) => {
    const share = divide(sideTotal, totalWeight)
    const marginPct = marginRatePct(dish, options.marginGuests, params)
    const shareWithMargin = multiply(share, add(ONE, divide(marginPct, HUNDRED)))
    const portions = ceilToUnits(shareWithMargin, dish.productionUnit)
    return {
      dish,
      share,
      shareWithMargin,
      marginPct,
      portions,
      productionUnits: portions / dish.productionUnit,
      recommendedPortions: portions,
      customerSet: false,
    }
  })
}

/**
 * Run 15 item 1(b): the quantity the customer set with − / +, in portions,
 * as the engine is willing to make it — raised to whole production units
 * (§6.7 step 5) and never below one unit, because half a batch does not
 * exist. `null` where the customer set nothing for this dish.
 */
export function customerQuantityFor(
  dish: Dish,
  quantities: Readonly<Record<string, number>> | undefined,
): number | null {
  const asked = quantities?.[dish.id]
  if (asked === undefined || !Number.isFinite(asked)) return null
  const unit = Math.max(Math.round(dish.productionUnit), 1)
  const whole = Math.ceil(Math.max(asked, 0) / unit) * unit
  return Math.max(whole, unit)
}

/**
 * The same line with the customer's own quantity on it. The share and the
 * margin are left exactly as the recommendation worked them out: they are
 * what *Vissza az ajánlotthoz* goes back to, and nothing downstream prices
 * off them (§5.11 keeps both off the page).
 */
function withCustomerQuantity(
  line: SideLine,
  quantities: Readonly<Record<string, number>> | undefined,
): SideLine {
  const asked = customerQuantityFor(line.dish, quantities)
  if (asked === null) return line
  return {
    ...line,
    portions: asked,
    productionUnits: asked / line.dish.productionUnit,
    customerSet: true,
  }
}

export interface SideAllocation {
  /** The dishes the customer chose, with what is made of each. */
  lines: SideLine[]
  /** §6.7 step 6: the portions Planteen composes for the gluten-free guests. */
  glutenFreeLines: SideLine[]
  /** Every line on the side, chosen and gluten-free. */
  all: SideLine[]
  /** Pieces per guest on this side, after the balance. */
  piecesPerGuest: Rational
  /** The side total made, across every line. */
  portions: number
  /** The part of it packed and labelled separately. */
  glutenFreePortions: number
  /**
   * True where the side has gluten-free guests and no eligible dish for the
   * date. §6.7 step 6: the order cannot go ahead on the automatic lane.
   */
  glutenFreeUnavailable: boolean
}

const sumPortions = (lines: readonly SideLine[]): number =>
  lines.reduce((sum, line) => sum + line.portions, 0)

/**
 * §6.7 step 6, first half: how many of the eligible dishes the gluten-free
 * portions are split across. "As few dishes as sensible": as many as keep
 * each at one production unit or more.
 *
 * The divisor is the **largest** production unit among the eligible dishes,
 * not each dish's own, because the portions are split equally and the split
 * has to keep the largest-unit dish whole. §6.7 does not say which; this is
 * the conservative reading and it is the customer page's (vB6).
 */
function glutenFreeDishCount(
  eligible: readonly Dish[],
  piecesPer: Rational,
  glutenFreeGuests: number,
  marginGuests: number,
  params: QuantityParams,
): number {
  const first = eligible[0]
  if (first === undefined) return 0
  const need = multiply(
    multiply(rational(glutenFreeGuests), piecesPer),
    add(ONE, divide(marginRatePct(first, marginGuests, params), HUNDRED)),
  )
  const largestUnit = eligible.reduce((largest, dish) => Math.max(largest, dish.productionUnit), 1)
  const fit = floorToInteger(divide(need, rational(largestUnit)))
  return Math.max(1, Math.min(eligible.length, fit))
}

/**
 * §6.7 steps 1–6 for one side.
 *
 * With no gluten-free guests this is steps 1–5 and nothing else. With them,
 * step 6: the gluten-free portions come out of the eligible dishes, and the
 * chosen dishes give up the same quantity, so the side stays within one
 * production unit of the same order with no gluten-free guests. Gluten-free
 * guests are part of the guest count, never added to it.
 */
export function allocateSide(
  args: {
    dishes: readonly Dish[]
    piecesPerGuest: Rational
    guests: number
    glutenFreeGuests: number
    /** Run 15 item 1: dish ID → the quantity the customer set with − / +. */
    quantities?: Readonly<Record<string, number>>
    glutenFreeEligible?: readonly Dish[]
  },
  params: QuantityParams,
): SideAllocation {
  const { piecesPerGuest: piecesPer, guests } = args
  const quantities = args.quantities
  const glutenFreeGuests = Math.min(Math.max(args.glutenFreeGuests, 0), guests)
  const eligible = args.glutenFreeEligible ?? []

  /* Steps 1-5: the order as if no guest were gluten-free. Run 15 item 1(e):
     every dish is worked out on its recommendation, and the customer's own
     quantities are laid on top afterwards — so a dish the customer changed
     takes nothing away from the others and gives nothing to them. */
  const full = recommendedLines(
    args.dishes,
    piecesPer,
    guests,
    { marginGuests: guests },
    params,
  ).map((line) => withCustomerQuantity(line, quantities))

  const empty = (overrides: Partial<SideAllocation> = {}): SideAllocation => ({
    lines: [],
    glutenFreeLines: [],
    all: [],
    piecesPerGuest: piecesPer,
    portions: 0,
    glutenFreePortions: 0,
    glutenFreeUnavailable: false,
    ...overrides,
  })

  if (glutenFreeGuests === 0 || isZero(piecesPer)) {
    const lines = full.filter((line) => line.portions > 0)
    return empty({ lines, all: lines, portions: sumPortions(lines) })
  }

  /* §6.7 step 6: a side with no eligible dish for the date cannot be made. */
  if (eligible.length === 0) {
    const lines = full.filter((line) => line.portions > 0)
    return empty({
      lines,
      all: lines,
      portions: sumPortions(lines),
      glutenFreeUnavailable: true,
    })
  }

  if (params.glutenFreeAllocation === 'gf_box_per_side') {
    return glutenFreeBoxPerSide(
      { dishes: args.dishes, piecesPer, guests, glutenFreeGuests, quantities, eligible },
      params,
    )
  }

  /* Step 6, first half: the gluten-free portions, split equally across as few
     eligible dishes as keep each at one production unit or more. They take
     the margin band of the whole guest count. Planteen composes them, so a
     customer quantity never reaches them (run 15 item 1(g)). */
  const count = glutenFreeDishCount(eligible, piecesPer, glutenFreeGuests, guests, params)
  const glutenFreeLines = recommendedLines(
    eligible.slice(0, count),
    piecesPer,
    glutenFreeGuests,
    { marginGuests: guests },
    params,
    /* Run 17 item B5: a whole gluten-free line is the gluten-free guests'
       in full, here as in `gf_box_per_side`, so every reader of the share
       reads one field whichever mode the register names. */
  ).map((line) => ({ ...line, glutenFreePortions: line.portions }))

  const target = sumPortions(full) - sumPortions(glutenFreeLines)
  if (glutenFreeGuests >= guests || target <= 0 || args.dishes.length === 0) {
    return empty({
      glutenFreeLines,
      all: glutenFreeLines,
      portions: sumPortions(glutenFreeLines),
      glutenFreePortions: sumPortions(glutenFreeLines),
    })
  }

  /* Step 6, second half: the chosen dishes give up what the gluten-free
     portions add. Each first takes its share rounded DOWN to whole production
     units, at least one unit; then whole units go to the dish furthest below
     its share until the side total is met. Rounding each reduced share up,
     dish by dish, added food on top of the guest count — the first version of
     the rule, which §6.7 records and rejects. */
  const remainingShare = rational(guests - glutenFreeGuests, guests)
  const want = full.map((line) => multiply(line.shareWithMargin, remainingShare))
  const lines: SideLine[] = full.map((line, index) => {
    const wanted = want[index] ?? ZERO
    /* Run 15 item 1(b): a dish the customer counted out is made in exactly
       that quantity. It neither gives up a share nor takes one. */
    const portions = line.customerSet
      ? line.portions
      : Math.max(line.dish.productionUnit, floorToUnits(wanted, line.dish.productionUnit))
    return {
      ...line,
      share: multiply(line.share, remainingShare),
      shareWithMargin: wanted,
      recommendedPortions: Math.max(
        line.dish.productionUnit,
        floorToUnits(wanted, line.dish.productionUnit),
      ),
      portions,
      productionUnits: portions / line.dish.productionUnit,
    }
  })

  /* The shortfall is measured in the dish's own production units (DR-9,
     option A, `build/FIXES.md` item 12): a dish three units short of its
     share is further below it than a dish three pieces short. */
  let guard = 0
  while (sumPortions(lines) < target && guard < 1000) {
    guard += 1
    let best = lines.findIndex((line) => !line.customerSet)
    if (best === -1) break
    let bestGap = rational(-1n)
    lines.forEach((line, index) => {
      if (line.customerSet) return
      const gap = divide(
        subtract(want[index] ?? ZERO, rational(line.portions)),
        rational(line.dish.productionUnit),
      )
      if (compare(gap, bestGap) > 0) {
        bestGap = gap
        best = index
      }
    })
    const chosen = lines[best]
    if (chosen === undefined || chosen.customerSet) break
    chosen.portions += chosen.dish.productionUnit
    chosen.productionUnits = chosen.portions / chosen.dish.productionUnit
  }

  const kept = lines.filter((line) => line.portions > 0)
  const all = [...kept, ...glutenFreeLines]
  return {
    lines: kept,
    glutenFreeLines,
    all,
    piecesPerGuest: piecesPer,
    portions: sumPortions(all),
    glutenFreePortions: sumPortions(glutenFreeLines),
    glutenFreeUnavailable: false,
  }
}

/**
 * Run 9, item 5 (DECIDED 20261010), `gf_allocation = gf_box_per_side`.
 *
 * - The gluten-free guests get **one labelled box per side**, holding
 *   gluten-free guests × that side's pieces per guest (§6.7 step 1, after the
 *   balance), rounded up to whole pieces or portions.
 * - The box is made of one dish below `gf_dishes_large_from` gluten-free
 *   guests and up to `gf_dishes_per_side_large` from it.
 * - **Which dishes:** gluten-free dishes already in the offer first, in the
 *   order the customer has them; then the featured eligible dish; then the
 *   one with the smallest production unit; catalogue order breaks a tie.
 * - **Rounding** is to each dish's total — main and gluten-free together —
 *   and never to the gluten-free share alone.
 * - The gluten-free guests' food comes **out of** the main quantities
 *   (`gf_in_total`): the chosen dishes are made for the other guests only.
 *
 * Lines: a dish in the offer and in the box has a main line (its total less
 * the box share) and a gluten-free line (the box share). Its production units
 * sit on the main line, so the two lines add up to whole units and the
 * kitchen's sum per dish is right; the gluten-free line carries none. A dish
 * added only for the box has a gluten-free line alone, holding its whole
 * rounded total and its units.
 */
export function glutenFreeBoxDishes(
  chosen: readonly Dish[],
  eligible: readonly Dish[],
  glutenFreeGuests: number,
  params: QuantityParams,
): Dish[] {
  const count =
    glutenFreeGuests >= params.glutenFreeLargeFrom
      ? params.glutenFreeDishesPerSide[1]
      : params.glutenFreeDishesPerSide[0]
  const eligibleIds = new Set(eligible.map((dish) => dish.id))
  const chosenIds = new Set(chosen.map((dish) => dish.id))
  const inOffer = chosen.filter((dish) => eligibleIds.has(dish.id))
  const others = eligible
    .map((dish, index) => ({ dish, index }))
    .filter((entry) => !chosenIds.has(entry.dish.id))
    .sort(
      (a, b) =>
        (a.dish.featured ? 0 : 1) - (b.dish.featured ? 0 : 1) ||
        a.dish.productionUnit - b.dish.productionUnit ||
        a.index - b.index,
    )
    .map((entry) => entry.dish)
  return [...inOffer, ...others].slice(0, Math.max(count, 0))
}

/**
 * **Run 17 item B5(a) (DECIDED 20261015, Barna): the same rate as everyone.**
 *
 * What the gluten-free guests get of one dish: gluten-free guests × that
 * side's pieces per guest, divided across the dishes that carry the box,
 * **with that dish's own production margin** — the margin band of the whole
 * guest count, exactly as every other line takes it (§6.4) — and then
 * **rounded up to that dish's production unit**.
 *
 * Run 9's box took no margin at all and rounded to whole pieces, so a
 * gluten-free guest was served less than the others out of the same kitchen
 * and the figure was not a whole batch of anything. Anikó's order
 * PLT-261012-0019 is the case: two gluten-free guests, five sweet pieces.
 */
function glutenFreeShareOf(
  dish: Dish,
  piecesPer: Rational,
  glutenFreeGuests: number,
  marginGuests: number,
  across: number,
  params: QuantityParams,
): number {
  if (across <= 0) return 0
  const share = divide(multiply(rational(glutenFreeGuests), piecesPer), rational(across))
  const withMargin = multiply(
    share,
    add(ONE, divide(marginRatePct(dish, marginGuests, params), HUNDRED)),
  )
  return ceilToUnits(withMargin, dish.productionUnit)
}

function glutenFreeBoxPerSide(
  args: {
    dishes: readonly Dish[]
    piecesPer: Rational
    guests: number
    glutenFreeGuests: number
    quantities: Readonly<Record<string, number>> | undefined
    eligible: readonly Dish[]
  },
  params: QuantityParams,
): SideAllocation {
  const { dishes, piecesPer, guests, glutenFreeGuests, quantities, eligible } = args

  /* **Run 17 item B5(b): cover from the menu first.** Where a gluten-free,
     verified dish is already in the main selection on this side, the
     gluten-free guests' share is added to that dish's own quantity and no
     separate line is drawn. Only where the side carries no gluten-free dish
     at all does Planteen compose the box out of dishes that are not on the
     menu, which is run 9's rule unchanged for that case.

     The variety rule of run 9 still binds: `gf_dishes_per_side_*` is how many
     different things go into one gluten-free box. */
  const count = Math.max(
    glutenFreeGuests >= params.glutenFreeLargeFrom
      ? params.glutenFreeDishesPerSide[1]
      : params.glutenFreeDishesPerSide[0],
    0,
  )
  const eligibleIds = new Set(eligible.map((dish) => dish.id))
  const chosenIds = new Set(dishes.map((dish) => dish.id))
  const onMenu = dishes.filter((dish) => eligibleIds.has(dish.id)).slice(0, count)
  const box = onMenu.length > 0 ? onMenu : glutenFreeBoxDishes(dishes, eligible, glutenFreeGuests, params)
  const inBox = new Set(box.map((dish) => dish.id))
  const shareOf = new Map(
    box.map((dish) => [
      dish.id,
      glutenFreeShareOf(dish, piecesPer, glutenFreeGuests, guests, box.length, params),
    ]),
  )
  const perDish = box.length === 0 ? ZERO : divide(multiply(rational(glutenFreeGuests), piecesPer), rational(box.length))

  /* The chosen dishes, made for the guests who are not gluten-free. The
     margin band is the whole guest count's, as for every line (§6.4). */
  const main = recommendedLines(
    dishes,
    piecesPer,
    guests - glutenFreeGuests,
    { marginGuests: guests },
    params,
  )

  const lines: SideLine[] = []
  const glutenFreeLines: SideLine[] = []
  const boxLine = (dish: Dish, portions: number, productionUnits: number): SideLine => ({
    dish,
    share: perDish,
    shareWithMargin: perDish,
    marginPct: ZERO,
    portions,
    productionUnits,
    /* Run 15 item 1(g): the gluten-free box is Planteen's own composition.
       A customer quantity never reaches it, so the recommendation is what
       it is made in and the line is never the customer's. */
    recommendedPortions: portions,
    customerSet: false,
    boxPortions: portions,
    glutenFreePortions: portions,
  })

  /* What the gluten-free guests still need after the dishes on the menu have
     carried what they can. A dish the customer counted down below the share
     keeps run 15's rule — it is made in exactly that quantity — and what it
     cannot carry becomes a separate gluten-free line. */
  let shortfall = 0

  for (const line of main) {
    if (!inBox.has(line.dish.id)) {
      const own = withCustomerQuantity(line, quantities)
      if (own.portions > 0) lines.push(own)
      continue
    }
    /* **Item B5(b): one line.** The dish's quantity is what the other guests
       get plus the gluten-free guests' share, and the share is recorded on
       the line rather than split off into a second one. The customer's − / +
       still counts on that same total (run 15 item 1 and run 16 item 1), and
       the share it cannot cover is carried away as a shortfall. */
    const gfShare = shareOf.get(line.dish.id) ?? 0
    const recommendedTotal = line.portions + gfShare
    const asked = customerQuantityFor(line.dish, quantities)
    const total = asked === null ? recommendedTotal : asked
    const covered = Math.min(total, gfShare)
    shortfall += gfShare - covered
    if (total > 0) {
      lines.push({
        ...line,
        portions: total,
        productionUnits: total / line.dish.productionUnit,
        recommendedPortions: recommendedTotal,
        customerSet: asked !== null,
        glutenFreePortions: covered,
      })
    } else {
      shortfall += 0
    }
  }

  /* A box dish that is not on the menu is Planteen's own composition and is a
     separate gluten-free line, as it has always been. This is item B5(b)'s
     *no gluten-free dish on that side*. */
  for (const dish of box) {
    if (chosenIds.has(dish.id)) continue
    glutenFreeLines.push(boxLine(dish, shareOf.get(dish.id) ?? 0, (shareOf.get(dish.id) ?? 0) / dish.productionUnit))
  }

  /* Item B5(b)'s *any shortfall*. It is covered from the best eligible dish
     that is **not** on the menu — featured first, then the smallest
     production unit, then catalogue order — so a dish the customer counted
     down is not quietly counted back up. Where every eligible dish is on the
     menu, the shortfall goes to the first of them as its own gluten-free
     line: the gluten-free guests are fed, and run 15's rule governs the main
     quantity rather than Planteen's own gluten-free composition (item 1(g)). */
  if (shortfall > 0) {
    const spare =
      eligible
        .map((dish, index) => ({ dish, index }))
        .filter((entry) => !chosenIds.has(entry.dish.id) && !inBox.has(entry.dish.id))
        .sort(
          (a, b) =>
            (a.dish.featured ? 0 : 1) - (b.dish.featured ? 0 : 1) ||
            a.dish.productionUnit - b.dish.productionUnit ||
            a.index - b.index,
        )
        .map((entry) => entry.dish)[0] ?? box[0] ?? eligible[0]
    if (spare !== undefined) {
      const portions = Math.ceil(shortfall / spare.productionUnit) * spare.productionUnit
      glutenFreeLines.push(boxLine(spare, portions, portions / spare.productionUnit))
    }
  }

  const all = [...lines, ...glutenFreeLines]
  return {
    lines,
    glutenFreeLines,
    all,
    piecesPerGuest: piecesPer,
    portions: sumPortions(all),
    /* Run 17 item B5(b): the gluten-free guests' food is what the separate
       lines hold **and** what the dishes on the menu carry for them. */
    glutenFreePortions: all.reduce((sum, line) => sum + (line.glutenFreePortions ?? 0), 0),
    glutenFreeUnavailable: false,
  }
}

/**
 * §5.6: `fruit_per_guest` × guests, split equally across the items chosen,
 * rounded up to production units. Fruit sits outside pieces per guest.
 */
export function fruitQuantities(
  guests: number,
  items: readonly FruitItem[],
  params: QuantityParams,
): { item: FruitItem; portions: number; productionUnits: number }[] {
  if (items.length === 0) return []
  const each = divide(multiply(rational(guests), params.fruitPerGuest), rational(items.length))
  return items.map((item) => {
    const portions = ceilToUnits(each, item.productionUnit)
    return { item, portions, productionUnits: portions / item.productionUnit }
  })
}

/**
 * §5.6, as run 15 item 2 settles it: a drink arrives either as **a whole
 * quantity** or as a count per guest, and the line carries which.
 *
 * - **Run 15 item 2(a):** every drink the customer adds is a whole quantity.
 *   The stepper counts bottles and the step is the drink's production unit,
 *   so `portions` arrives already whole and nothing multiplies it by the
 *   headcount — which is what makes a headcount change leave it alone.
 * - **`build/FIXES.md` items 74 and 78, kept by run 15 item 2(c):** a line
 *   the build works out for itself — the conference refreshment drink, the
 *   coffee line — is still per guest and still arrives with its own
 *   `portions`, rounded to whole catalogue units rather than to whole drinks.
 * - A line with no `portions` at all is `perGuest × guests`, as §5.6 had it.
 *   Nothing the page sends takes that road today; it is what lets an older
 *   client, and a stored order placed under the old model, read as it was.
 */
export function drinkQuantities(
  guests: number,
  drinks: readonly DrinkSelection[],
): { drink: DrinkSelection; portions: number; productionUnits: number }[] {
  return drinks.map((drink) => {
    const portions =
      drink.portions === undefined
        ? ceilToInteger(multiply(rational(guests), fromDecimal(drink.perGuest ?? 0)))
        : Math.max(Math.round(drink.portions), 0)
    return { drink, portions, productionUnits: drink.productionUnits ?? portions }
  })
}

/* ------------------------------------------------------------------ *
 * The conference drinks (build/FIXES.md items 74 and 78, F3 and F7)
 * ------------------------------------------------------------------ */

/**
 * `build/FIXES.md` item 74 (F3), Barna's ruling 3 of 8 October 2026:
 *
 *   *Refreshment drink (Balázs, 2 dl per head per occasion) for the
 *   conference elements: guests × 0.2 l × (welcome + breaks + lunch),
 *   rounded up to whole catalogue units using the unit volume.*
 *
 * `occasions` is `conferenceElements(selection).total` — named there once so
 * F3 and F7 count the same day — and both figures are passed in: the litres
 * per head come from the register and the unit volume from the catalogue row.
 *
 * **Null when the unit volume is not known**, which is the state the
 * catalogue is in: there is no volume field on a dish, so there is nothing to
 * divide by and F3's line stays hidden. The brief forbids inventing one, and
 * dividing by a guessed bottle size would be exactly that. Null is not an
 * error — it is *we cannot say yet*, and the caller hides the line.
 */
export function refreshmentDrinkQuantity(args: {
  guests: number
  occasions: number
  litresPerHeadPerOccasion: Rational
  unitVolumeLitres: Rational | null
}): { litres: Rational; units: number } | null {
  const { guests, occasions, litresPerHeadPerOccasion, unitVolumeLitres } = args
  if (unitVolumeLitres === null || isZero(unitVolumeLitres)) return null
  if (guests <= 0 || occasions <= 0) return null
  const litres = multiply(
    multiply(rational(Math.max(guests, 0)), litresPerHeadPerOccasion),
    rational(Math.max(occasions, 0)),
  )
  return { litres, units: ceilToInteger(divide(litres, unitVolumeLitres)) }
}

/**
 * `build/FIXES.md` item 78 (F7), ruling 3, **as run 16 item 8 rewrites it**
 * (PROVISIONAL 2026-10-15, Barna, pending Balázs):
 *
 *   *Coffee per guest is set per break by time of day: 0.8 cups per guest for
 *   a break starting before 12:00, 0.5 for a break starting at or after
 *   12:00.*
 *
 * So the day is counted twice over — how many of its coffee occasions start
 * before noon and how many at noon or after — and each count takes its own
 * figure. Ruling 3's single `coffee_per_head_per_occasion` is gone from the
 * arithmetic; the register keeps the key, with no row and no reader.
 *
 * `cupsPerUnit` is the catalogue row's production unit — F7 calls it *cups
 * per unit* — so `portions` is the whole units' worth of coffee, which is
 * what is made and what is charged for. `cups` is what the day actually
 * needs, before the rounding, and is never shown to the customer (§5.11
 * keeps production units off the page); it is here so a test can see the two
 * apart. **The rounding is the one F7 already had**: up to whole production
 * units (item 8(b), *follows the existing coffee rule*).
 *
 * **Null when a figure the day needs is missing**, which is the state today:
 * there is no coffee row in the catalogue (`coffee_dish_id` is empty). A
 * per-head figure is only needed where its own count is above zero, so an
 * afternoon-only day is priced without a morning figure and the other way
 * round.
 */
export function coffeeQuantity(args: {
  guests: number
  /** How many of the day's coffee occasions start before 12:00, and at or after. */
  occasions: { morning: number; afternoon: number }
  perHeadMorning: Rational | null
  perHeadAfternoon: Rational | null
  cupsPerUnit: number | null
}): { cups: Rational; units: number; portions: number } | null {
  const { guests, occasions, perHeadMorning, perHeadAfternoon, cupsPerUnit } = args
  if (cupsPerUnit === null || !Number.isInteger(cupsPerUnit) || cupsPerUnit <= 0) return null
  if (guests <= 0) return null
  const morning = Math.max(Math.trunc(occasions.morning) || 0, 0)
  const afternoon = Math.max(Math.trunc(occasions.afternoon) || 0, 0)
  if (morning + afternoon <= 0) return null
  if (morning > 0 && (perHeadMorning === null || isZero(perHeadMorning))) return null
  if (afternoon > 0 && (perHeadAfternoon === null || isZero(perHeadAfternoon))) return null

  const share = (count: number, perHead: Rational | null): Rational =>
    count <= 0 || perHead === null ? ZERO : multiply(perHead, rational(count))

  const perGuest = add(share(morning, perHeadMorning), share(afternoon, perHeadAfternoon))
  if (isZero(perGuest)) return null
  const cups = multiply(rational(guests), perGuest)
  const portions = ceilToUnits(cups, cupsPerUnit)
  return { cups, units: portions / cupsPerUnit, portions }
}
